// ============================================
// Debug UV Visualization Effect
// Shows UV coordinates, grid, and corner markers
// ============================================

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Debug effect that visualizes UV coordinates and geometry
 * 
 * Shows:
 * - UV as color (R=U, G=V)  
 * - Grid lines every 10%
 * - Center cross (yellow)
 * - Edge lines (magenta)
 * - Corner markers: Red=BL, Green=BR, Blue=TL, White=TR
 */
export class DebugUVEffect extends PostProcessEffect {
  // Must match the registry key in effects/index.ts ('debugUV') — with the old 'debug-uv' the
  // reconcile pool never matched, so the effect was disposed and recreated on every sync.
  readonly type = 'debugUV';
  readonly displayName = 'Debug UV';
  
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  
  getParameters(): PostProcessParameter[] {
    return [];
  }
  
  getParameterValue(_name: string): number | boolean | number[] {
    return 0;
  }
  
  setParameterValue(_name: string, _value: number | boolean | number[]): void {
    // No parameters
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const debugUvWgsl = getCompiledShader('postprocess/debug-uv');
    this.shader = ctx.createShader(debugUvWgsl, debugUvWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'debug-uv-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'debug-uv-ldr',
    });
  }
  
  // Dummy uniform data (needed for WebGPU bind group)
  private uniformData = new Float32Array(4); // vec4 padding
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, isHdrTarget } = context;
    
    const pipeline = isHdrTarget ? this.pipelineHDR : this.pipelineLDR;
    if (pipeline === NULL_HANDLE) return;
    
    // NOTE: PostProcessStack already set render target and viewport
    this.fullscreenPass(ctx, pipeline, this.uniformData, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
